import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect } from 'react';
import { Form, FormLabel } from './';
import { useField } from 'formik';
import * as Yup from 'yup';
import { FormattedMessage } from 'react-intl';
import { TrashIcon } from '@heroicons/react/outline';
import { CheckCircleIcon } from '@heroicons/react/solid';


export const FormSelectList = props => {

    const [field, meta] = useField(props.name);
    const [inputOptions, setInputOptions] = useState([]);
    const initValues = {item: ''};

    const [inputFields, setInputFields] = useState([
        {
            name: 'item', type: 'select', placeholder: props.inputPlaceholder, label: props.inputLabel,
            options: props.inputOptions, notLabels: props.notLabels
        }
    ]);

    const validationSchema = {
        item: Yup.string()
            .oneOf(props.inputOptions.map(o => o.value))
            .required(<FormattedMessage id="sv.validation.required" />)
    };

    const handleSubmit = useCallback((values, actions) => {
        const newValue = [...field.value];
        newValue.push(values.item);
        props.onChange(props.name, newValue, true);
        const newInputOptions = (inputOptions.length > 0 ? [...inputOptions] : [...props.inputOptions]).filter(o => o.value !== values.item);
        setInputOptions(newInputOptions);
        setInputFields([{...inputFields[0], options: newInputOptions}]);
        actions.resetForm();
    }, [inputFields]);

    const removeItem = idx => {
        const newValue = [...field.value];
        const removableValue = newValue[idx];
        newValue.splice(idx, 1);
        props.onChange(props.name, newValue, true);
        const newInputOptions = [...inputOptions];

        if (newInputOptions.findIndex(o => o.value === removableValue) === -1) {
            newInputOptions.push(props.inputOptions.find(o => o.value === removableValue));
        }

        setInputOptions(newInputOptions);
        setInputFields([{...inputFields[0], options: newInputOptions}]);
    };

    useEffect(() => {

        if (inputOptions.length === 0) {
            const newInputOptions = [...props.inputOptions];

            field.value.forEach(i => {
                const idx = newInputOptions.findIndex(o => o.value === i);
                if (idx !== -1) {
                    newInputOptions.splice(idx, 1);
                }
            });

            setInputOptions(newInputOptions);
            setInputFields([{...inputFields[0], options: newInputOptions}]);
        }
    }, [field.value]);

    //a stored value that is no longer in the option list (an old language code, for example)
    //used to crash the whole profile screen on render
    const displayFor = value => {
        const option = props.inputOptions.find(o => o.value === value);
        return option ? option.display : value;
    };

    return (
        <div>
            <FormLabel label={props.label} name={props.name} />
            <div className="rounded shadow-sm border p-3">
                {(field.value || []).map((input, idx) => (
                        <div key={props.key + '_' + idx} className="flex mt-3">
                            <CheckCircleIcon className="w-5 h-6 text-green-500 mr-3 flex-none" />
                            <div className="break-all">{displayFor(input)}</div>
                            <div className="flex-grow flex flex-row-reverse">
                                <TrashIcon className="w-5 h-6 ml-5 text-gray-500 cursor-pointer hover:text-sv-p-d"
                                           onClick={() => removeItem(idx)} />
                            </div>
                        </div>
                    )
                )}
                <Form
                    className="mt-3"
                    enableReinitialize={true}
                    initValues={initValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    fields={inputFields}
                    noSubmitButton
                    submitBtnSmall
                    submitBtnSecondary
                    submitBtnLabel="sv.btn.add"
                />
            </div>
            {/* this input had no error slot at all, so a validation failure on it left the
                surrounding form unsaveable with nothing on screen to explain why */}
            {meta.error &&
                <div className="text-red-500 text-sm text-left mt-1">{meta.error}</div>
            }
        </div>
    );
};

FormSelectList.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    key: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    inputPlaceholder: PropTypes.string.isRequired,
    inputLabel: PropTypes.string.isRequired,
    inputOptions: PropTypes.array.isRequired,
    meta: PropTypes.object.isRequired
};

